import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/icon-button/icon-button.js";
import "@shoelace-style/shoelace/dist/components/resize-observer/resize-observer.js";

import { html, PropertyValues } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { litIf } from "../LitUtils";
import { Style } from "../Styles";
import { BaseElement } from "../BaseElement";
import { LightTheme } from "../ThemeLight";
import { Normalize } from "../Style/Normalize";
import { styles } from "./PageHeader.styles";

import "../SearchBar";

export type MenuItem = {
    name: string;
    href: string;
    subMenu?: MenuItem[];
};

const DEFAULT_MENU = [
    {
        name: "Explore",
        href: "/explore/",
    },
    {
        name: "News",
        href: "/post/",
    },
];

@customElement("ds-page-header")
export class PageHeader extends BaseElement {
    static override styles = [Normalize, LightTheme, Style, styles];
    @property({ type: String }) logo?: string;
    @property({ type: String }) logoDark?: string;
    @property({ type: String }) icon?: string;
    @property({ type: String }) background?: string;
    @property({ type: String }) homeUrl = "/";
    @property({ type: Boolean }) showSearch = false;
    @property({ type: String }) type: "fat" | "thin" | "menu" = "thin";

    @query(".banner") protected _bannerElement?: HTMLElement;
    @query(".background-image") protected _imageElement?: HTMLElement;
    @query(".content-container") protected _contentElement?: HTMLElement;

    private FAT_SIZES = [
        { minWidth: 0, height: 312 },
        // { minWidth: 640, height: 20 },
        // { minWidth: 768, height: 30 },
        // { minWidth: 1024, height: 40 },
        { minWidth: 1280, height: 800 },
    ];

    private THIN_SIZES = [
        { minWidth: 0, height: 312 },
        // { minWidth: 640, height: 20 },
        // { minWidth: 768, height: 30 },
        // { minWidth: 1024, height: 40 },
        { minWidth: 1280, height: 312 },
    ];

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
    }
    protected override firstUpdated(_changedProperties: PropertyValues): void {
        if (this._bannerElement) {
            const resizeObserver = this._bannerElement.querySelector("sl-resize-observer");
            if (resizeObserver) {
                resizeObserver.addEventListener("sl-resize", (event) => {
                    const detail = (event as CustomEvent).detail;
                    if (detail.entries.length > 0) {
                        const sizes = this.type === "fat" ? this.FAT_SIZES : this.THIN_SIZES;

                        const width = detail.entries[0].contentRect.width;

                        let high;
                        let low;
                        for (let i = 0; i < sizes.length - 1; ++i) {
                            const s = sizes[i];
                            const n = sizes[i + 1];

                            if (width > s.minWidth && width < n.minWidth) {
                                high = n;
                                low = s;
                                break;
                            }
                        }

                        if (!high) {
                            low = high = sizes[sizes.length - 1];
                        }

                        if (
                            low &&
                            high &&
                            this._bannerElement &&
                            this._imageElement &&
                            this._contentElement
                        ) {
                            const rh = high.height - low.height;
                            let h = low.height;
                            const rw = high.minWidth - low.minWidth;
                            const a = (width - low.minWidth) / rw;
                            if (rw > 0) {
                                h = low.height + rh * a;
                            }

                            this._bannerElement.style.height = `${h}px`;
                            this._imageElement.style.height = `${h}px`;
                            this._contentElement.style.height = `${h}px`;
                        }
                    }
                });
            }
        }
    }

    private _renderRightNav() {
        return html`<div class="container">
            <div class="menu-items flex-row">
                ${DEFAULT_MENU.map(
                    (x: MenuItem) =>
                        html`<a href=${x.href}
                            >${x.name} <sl-icon name="chevron-down"></sl-icon
                        ></a>`
                )}
                <sl-button pill size="medium">Log in</sl-button>
            </div>
        </div>`;
    }

    private _renderSmallRightNav() {
        return html`<div class="container">
            <div class="menu-items flex-row">
                <sl-icon-button name="list" class="menu-icon"></sl-icon-button>
            </div>
        </div>`;
    }

    private _renderLogo() {
        return html`<a class="logo" href=${this.homeUrl}>
            <img src="${(this.type === "menu" ? this.logoDark : this.logo) as string}"
        /></a>`;
    }

    private _renderIcon() {
        return html`<a class="icon" href=${this.homeUrl}> <img src="${this.icon as string}" /></a>`;
    }

    private _renderSmall() {
        return html`<div class="small-screen container">
            ${this._renderIcon()} ${this._renderSmallRightNav()}
        </div>`;
    }

    private _renderMedium() {
        return html`<div class="medium-screen container">
            ${this._renderLogo()} ${this._renderSmallRightNav()}
        </div>`;
    }

    private _renderLarge() {
        return html`<div class="large-screen container">
            ${this._renderLogo()} ${this._renderRightNav()}
        </div>`;
    }

    private _renderExtraLarge() {
        return html`<div class="xlarge-screen container">
            ${this._renderLogo()} ${this._renderRightNav()}
        </div>`;
    }

    override render() {
        return html`
            <div class="banner">
                <sl-resize-observer>
                    ${litIf(
                        this.type !== "menu",
                        () =>
                            html` <img
                                    alt="splashscreen"
                                    class="background-image"
                                    src=${ifDefined(this.background)}
                                />
                                <div class="content-container">
                                    <div class="content flex-column gap-1">
                                        ${litIf(
                                            this.showSearch,
                                            () => html`
                                                <h1 class="cta">${this.title}</h1>
                                                <ds-search-bar
                                                    size="large"
                                                    placeholder="Search by country, city or dive site name"
                                                ></ds-search-bar>
                                                <a>Explore nearby</a>
                                            `,
                                            () => html`<h1 class="cta">${this.title}</h1>`
                                        )}
                                    </div>
                                </div>`
                    )}

                    <div class="header ${this.type}">
                        ${this._renderSmall()} ${this._renderMedium()} ${this._renderLarge()}
                        ${this._renderExtraLarge()}
                    </div>
                </sl-resize-observer>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-page-header": PageHeader;
    }
}
